import Input from "../Input";
import { ComboBoxOwnProps } from ".";
import Autocomplete from "@mui/material/Autocomplete";
import { Chip, Box, Typography, Grid, Checkbox } from "@mui/material";

export type ComboBoxProps = ComboBoxOwnProps &
	Omit<React.ComponentProps<typeof Autocomplete>, "renderInput">;

export default function ComboBox({
	options = [],
	ChipProps,
	InputProps,
	groupSelectAll,
	...rest
}: ComboBoxProps) {
	// `value`, `onChange` and `multiple` are supplied by the redux-form wrapper
	// and still flow into Autocomplete via {...rest}; we only read them here to
	// drive the per-group "Select all" checkbox.
	const { value, onChange, multiple } = rest as any;

	const renderGroupWithSelectAll = (params: any) => {
		const groupOptions = (options as any[]).filter(
			(o) => o?.group === params.group
		);
		const selectedValues: any[] = Array.isArray(value) ? value : [];
		const isAllSelected =
			groupOptions.length > 0 &&
			groupOptions.every((go) =>
				selectedValues.some((v) => v?.value === go.value)
			);
		const isSomeSelected =
			!isAllSelected &&
			groupOptions.some((go) =>
				selectedValues.some((v) => v?.value === go.value)
			);

		const toggleSelectAll = () => {
			const next = isAllSelected
				? selectedValues.filter(
						(v) => !groupOptions.some((go) => go.value === v?.value)
				  )
				: [
						...selectedValues,
						...groupOptions.filter(
							(go) => !selectedValues.some((v) => v?.value === go.value)
						),
				  ];
			onChange?.(null, next);
		};

		return (
			<li key={params.key}>
				<Box
					sx={(theme) => ({
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						pl: 1.5,
						pr: 0.5,
						position: "sticky",
						top: 0,
						zIndex: 1,
						// Solid base so scrolled options never show through the
						// sticky header; the selected tint is layered on top.
						bgcolor: "background.paper",
						backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
						borderBottom: "1px solid",
						borderColor: "divider",
					})}
				>
					<Typography
						variant="subtitle2"
						color="text.primary"
						sx={{ fontWeight: 600 }}
					>
						{params.group}
					</Typography>
					<Checkbox
						size="small"
						checked={isAllSelected}
						indeterminate={isSomeSelected}
						onChange={toggleSelectAll}
						onMouseDown={(e) => e.preventDefault()}
					/>
				</Box>
				<ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
			</li>
		);
	};

	const groupProps = groupSelectAll
		? {
				groupBy: (option: any) => option?.group ?? "",
				...(multiple ? { renderGroup: renderGroupWithSelectAll } : {}),
		  }
		: {};

	return (
		<Autocomplete
			{...rest}
			{...groupProps}
			options={options}
			getOptionLabel={(option: any) =>
				typeof option === "string" ? option : option?.label ?? ""
			}
			isOptionEqualToValue={(option: any, value: any) =>
				option?.value === value?.value
			}
			renderTags={(selectedOptions: any[], getTagProps) =>
				selectedOptions.map((option: any, index: number) => (
					<Chip
						label={typeof option === "string" ? option : option?.label}
						variant="filled"
						size="small"
						{...getTagProps({ index })}
						{...ChipProps}
						key={index}
					/>
				))
			}
			renderOption={(props, option: any) => (
				<Box component="li" {...props} key={option.value}>
				<Grid container justifyContent="space-between" width="100%">
				  <Grid item>
					<Typography variant="body2">{option.label}</Typography>
				  </Grid>
				  <Grid item>
					<Typography variant="caption" color="text.secondary">
					  {option.status}
					</Typography>
				  </Grid>
				</Grid>
			  </Box>
			)}
			renderInput={(params) => (
				<Input
					{...params}
					{...InputProps}
					inputProps={{
						autoComplete: "new-password",
						form: { autoComplete: "off" },
						...params.inputProps,
						...InputProps?.inputProps,
					}}
				/>
			)}
		/>
	);
}
